import { limitar, vetor, type Vetor3 } from '../geometria/vetor3'
import { danoNaRegiao, type DanoVeicular, type RegiaoDanoVeicular } from './danoVeicular'
import { criarDescritorDeformacao, type DescritorDeformacaoVeicular } from './deformacaoVeicular'
import { HATCH_COMPACTO, SEDAN_MEDIO, type PerfilVeiculo } from './perfilVeiculo'
import {
  amostrarCinematica,
  colisao,
  duracaoRealS,
  posicaoDoVeiculo,
  velocidadeDoEvento,
  velocidadeRealMs,
  type TipoColisao,
} from './timelineCinematica'

/**
 * Colisão entre dois veículos, como modelo VISUAL didático.
 *
 * Não é reconstrução pericial. Serve para que a cena mostre, com números
 * coerentes entre si, o que a aula quer mostrar:
 *
 * - a velocidade que importa é a RELATIVA (50 → e 30 → é um toque; 50 → e
 *   ← 30 é um impacto de 80);
 * - cada veículo sofre uma variação de velocidade (ΔV) inversamente
 *   proporcional à própria massa — o hatch contra o SUV deforma mais;
 * - o ponto de contato decide a região atingida em CADA veículo, e o braço
 *   de alavanca entre ele e o centro decide a rotação depois do impacto.
 *
 * O veículo A é sempre o da maquete (o que tem ocupantes); o B é o outro.
 * A timeline de A continua sendo a autoral — o modelo só acrescenta a B um
 * movimento próprio e a A um pequeno ajuste visual pós-impacto.
 */

export type ClasseVeiculo = 'compacto' | 'medio' | 'suv'

export interface DefinicaoClasseVeiculo {
  id: ClasseVeiculo
  nome: string
  /** massa aproximada em ordem de marcha, kg — ordem de grandeza, não ficha técnica */
  massaKg: number
  /** geometria base usada para desenhar a classe */
  perfilId: PerfilVeiculo['id']
  /** escala visual sobre a geometria base (o SUV é o sedan mais alto) */
  escala: [number, number, number]
  /** altura do centro do para-choque: onde ele encontra a lateral do outro */
  alturaParaChoqueY: number
  /** quanto a frente desta classe agride a estrutura do outro (didático) */
  agressividade: number
}

export const CLASSES_VEICULO: Record<ClasseVeiculo, DefinicaoClasseVeiculo> = {
  compacto: {
    id: 'compacto',
    nome: 'Hatch compacto',
    massaKg: 1100,
    perfilId: 'hatchCompacto',
    escala: [1, 1, 1],
    alturaParaChoqueY: 0.48,
    agressividade: 0.95,
  },
  medio: {
    id: 'medio',
    nome: 'Sedan médio',
    massaKg: 1350,
    perfilId: 'sedanMedio',
    escala: [1, 1, 1],
    alturaParaChoqueY: 0.5,
    agressividade: 1,
  },
  suv: {
    id: 'suv',
    nome: 'SUV',
    massaKg: 1900,
    perfilId: 'sedanMedio',
    escala: [1.04, 1.16, 1.02],
    alturaParaChoqueY: 0.64,
    agressividade: 1.15,
  },
}

export const CLASSES_VEICULO_ORDEM: readonly ClasseVeiculo[] = ['compacto', 'medio', 'suv']

export const classeDoPerfil = (perfil: PerfilVeiculo): ClasseVeiculo =>
  perfil.id === 'hatchCompacto' ? 'compacto' : 'medio'

export const perfilDaClasse = (classe: ClasseVeiculo): PerfilVeiculo =>
  CLASSES_VEICULO[classe].perfilId === 'hatchCompacto' ? HATCH_COMPACTO : SEDAN_MEDIO

/** Como o veículo A (o da maquete) encontra o B quando A é quem se move de frente. */
export type ArranjoFrontalEntreVeiculos = 'frenteAFrente' | 'frenteContraLateral' | 'frenteContraTraseira'

export type ArranjoEntreVeiculos =
  | ArranjoFrontalEntreVeiculos
  /** B bate na lateral de A (colisão lateral da timeline) */
  | 'lateralRecebida'
  /** B bate na traseira de A (colisão traseira / 2º contato do engavetamento) */
  | 'traseiraRecebida'
  /** abalroamento: as laterais se raspam com os dois em movimento */
  | 'raspagemLateral'

export type PontoContatoLateral = 'eixoDianteiro' | 'portaDianteira' | 'colunaB' | 'portaTraseira' | 'eixoTraseiro'
export type AlinhamentoFrontal = 'central' | 'deslocado'
/** no abalroamento: o outro veículo segue no mesmo sentido (troca de faixa) ou vem em sentido oposto */
export type SentidoRaspagem = 'mesmoSentido' | 'sentidoOposto'

export const PONTOS_CONTATO_LATERAL: readonly { id: PontoContatoLateral; nome: string }[] = [
  { id: 'eixoDianteiro', nome: 'Eixo/roda dianteira' },
  { id: 'portaDianteira', nome: 'Porta dianteira' },
  { id: 'colunaB', nome: 'Coluna B' },
  { id: 'portaTraseira', nome: 'Porta traseira' },
  { id: 'eixoTraseiro', nome: 'Eixo/roda traseira' },
]

export const ARRANJOS_FRONTAIS: readonly { id: ArranjoFrontalEntreVeiculos; nome: string; descricao: string }[] = [
  {
    id: 'frenteAFrente',
    nome: 'Frente × frente',
    descricao: 'Os dois vêm em sentidos opostos: as velocidades se SOMAM e as duas dianteiras encurtam.',
  },
  {
    id: 'frenteContraLateral',
    nome: 'Frente × lateral',
    descricao: 'O veículo da maquete atinge a lateral do outro, que cruza a via: frente de um, porta e coluna do outro.',
  },
  {
    id: 'frenteContraTraseira',
    nome: 'Frente × traseira',
    descricao: 'Mesmo sentido: só a DIFERENÇA de velocidades vira impacto, e o da frente é empurrado adiante.',
  },
]

export interface ConfiguracaoOutroVeiculo {
  /** nulo = a mesma classe do veículo da maquete */
  classe: ClasseVeiculo | null
  /** velocidade do outro veículo quando é o da maquete quem se move de frente, km/h */
  velocidadeKmh: number
  arranjoFrontal: ArranjoFrontalEntreVeiculos
  /** onde a frente de quem bate encontra a lateral de quem é atingido */
  pontoLateral: PontoContatoLateral
  alinhamento: AlinhamentoFrontal
  sentidoRaspagem: SentidoRaspagem
}

export const CONFIGURACAO_OUTRO_VEICULO_PADRAO: ConfiguracaoOutroVeiculo = {
  classe: null,
  velocidadeKmh: 50,
  arranjoFrontal: 'frenteAFrente',
  pontoLateral: 'portaDianteira',
  alinhamento: 'central',
  sentidoRaspagem: 'mesmoSentido',
}

export const VELOCIDADES_OUTRO_VEICULO: readonly number[] = [0, 20, 30, 50, 60, 80]

export const ID_VEICULO_PRINCIPAL = 'veiculoPrincipal'
export const ID_OUTRO_VEICULO = 'outroVeiculo'
export const ID_OUTRO_VEICULO_SECUNDARIO = 'outroVeiculoSecundario'

/** Z, no referencial do veículo, do ponto da lateral escolhido. */
export function zDoPontoLateral(perfil: PerfilVeiculo, ponto: PontoContatoLateral): number {
  switch (ponto) {
    case 'eixoDianteiro':
      return perfil.eixoDianteiroZ
    case 'portaDianteira':
      return (perfil.portaDianteira.zDianteiro + perfil.portaDianteira.zTraseiro) / 2
    case 'colunaB':
      return perfil.colunaB.z
    case 'portaTraseira':
      return (perfil.portaTraseira.zDianteiro + perfil.portaTraseira.zTraseiro) / 2
    case 'eixoTraseiro':
      return perfil.eixoTraseiroZ
  }
}

/** Qual arranjo o tipo de colisão da timeline representa quando o alvo é outro veículo. */
export function arranjoDoEvento(
  tipo: TipoColisao,
  config: ConfiguracaoOutroVeiculo,
  secundario = false,
): ArranjoEntreVeiculos | null {
  if (secundario) return colisao(tipo).impactoSecundario ? 'traseiraRecebida' : null
  if (tipo === 'abalroamento') return 'raspagemLateral'
  // o veículo desliza contra poste/muro: não há um segundo veículo
  if (tipo === 'lateralContraObjeto') return null
  const direcao = colisao(tipo).direcaoImpacto
  if (!direcao) return null
  if (Math.abs(direcao.x) > Math.abs(direcao.z)) return 'lateralRecebida'
  if (direcao.z > 0) return 'traseiraRecebida'
  return config.arranjoFrontal
}

// ---------------------------------------------------------------------------

interface Pose2D {
  x: number
  z: number
  /** guinada em torno de +Y; 0 = frente do veículo para +Z do mundo */
  yaw: number
}

/** Leva um ponto do referencial de um veículo (x, z) para o mundo. */
const paraMundo2D = (pose: Pose2D, x: number, z: number): { x: number; z: number } => ({
  x: pose.x + x * Math.cos(pose.yaw) + z * Math.sin(pose.yaw),
  z: pose.z - x * Math.sin(pose.yaw) + z * Math.cos(pose.yaw),
})

const doMundo2D = (pose: Pose2D, x: number, z: number): { x: number; z: number } => {
  const dx = x - pose.x
  const dz = z - pose.z
  return {
    x: dx * Math.cos(pose.yaw) - dz * Math.sin(pose.yaw),
    z: dx * Math.sin(pose.yaw) + dz * Math.cos(pose.yaw),
  }
}

export interface EntradaColisaoEntreVeiculos {
  tipo: TipoColisao
  perfilA: PerfilVeiculo
  /** velocidade do evento escolhida no painel, km/h */
  velocidadeEventoKmh?: number
  config: ConfiguracaoOutroVeiculo
  smallOverlap?: boolean
  /** o 2º contato do engavetamento */
  secundario?: boolean
  instanteContato: number
  duracaoDeformacao?: number
}

export interface ColisaoEntreVeiculos {
  arranjo: ArranjoEntreVeiculos
  tipo: TipoColisao
  velocidadeEventoKmh: number
  idA: string
  idB: string
  classeA: ClasseVeiculo
  classeB: ClasseVeiculo
  perfilA: PerfilVeiculo
  perfilB: PerfilVeiculo
  escalaB: [number, number, number]
  massaAKg: number
  massaBKg: number
  velocidadeAKmh: number
  velocidadeBKmh: number
  /** velocidade de aproximação ao longo da normal do contato */
  velocidadeRelativaKmh: number
  deltaVAKmh: number
  deltaVBKmh: number
  danoA: DanoVeicular
  danoB: DanoVeicular
  descritorA: DescritorDeformacaoVeicular
  descritorB: DescritorDeformacaoVeicular
  /** ponto de contato no mundo, no instante do contato */
  pontoContato: Vetor3
  /** normal do contato, de A para B (no plano do solo) */
  normal: Vetor3
  poseBContato: Pose2D
  /** distância do centro de B até a face de contato, ao longo da normal */
  distanciaFaceB: number
  /** ponto de contato no referencial de A */
  contatoLocalA: { x: number; z: number }
  velocidadeBAntes: Vetor3
  velocidadeBDepois: Vetor3
  guinadaBRadPorS: number
  /** empurrão e guinada pós-impacto de A, velocidade inicial (m/s) e taxa (rad/s) */
  velocidadeExtraA: Vetor3
  guinadaARadPorS: number
  instanteContato: number
  duracaoDeformacao: number
  duracaoRealS: number
  leitura: string
}

/**
 * Coeficiente de restituição entre dois veículos, pela velocidade de aproximação.
 *
 * Colisão entre carros é quase toda plástica: a energia vai para o esmagamento.
 * O pouco que sobra de elástico é maior nos toques lentos (para-choque e
 * suportes voltam) e quase some nas batidas fortes — da ordem de 0,4 abaixo de
 * 10 km/h e de 0,1 acima de 50 km/h. Ordem de grandeza didática, não perícia.
 */
export function restituicaoEntreVeiculos(aproximacaoKmh: number): number {
  return 0.08 + 0.42 * Math.exp(-Math.max(0, aproximacaoKmh) / 18)
}
/** ângulo com que o outro veículo fecha sobre a lateral no abalroamento (≈ 8°) */
const ANGULO_RASPAGEM_RAD = 0.14
/** num contato de raspão a lateral se afasta de volta, e as chapas só se arrastam */
const RESTITUICAO_RASPAGEM = 0.6
const ACOPLAMENTO_TANGENCIAL_RASPAGEM = 0.12
/** a raspagem amassa pouco: o que ela deixa é risco e um amassado raso */
const INTRUSAO_MAXIMA_RASPAGEM_M = 0.14
const ATRITO_G = 0.7 * 9.81
const TAU_GUINADA_S = 0.55
const DISTANCIA_MAXIMA_APROXIMACAO_M = 9

const FATOR_ESTRUTURA: Record<RegiaoDanoVeicular, number> = {
  frente: 1,
  traseira: 1.05,
  lateralEsquerda: 1.45,
  lateralDireita: 1.45,
  teto: 1,
}

/**
 * Resolve a colisão entre A (maquete) e B (outro veículo) no instante do contato.
 *
 * Tudo é derivado: velocidades, massas e o ponto de contato dão velocidade
 * relativa, ΔV de cada um, região e centro do dano de cada um e o movimento de
 * B depois do impacto. Função pura — o mesmo resultado para a mesma entrada.
 */
export function resolverColisaoEntreVeiculos(entrada: EntradaColisaoEntreVeiculos): ColisaoEntreVeiculos | null {
  const { tipo, perfilA, config } = entrada
  const arranjo = arranjoDoEvento(tipo, config, entrada.secundario)
  if (!arranjo) return null
  const velocidadeEvento = velocidadeDoEvento(tipo, entrada.velocidadeEventoKmh)
  const duracaoDeformacao = entrada.duracaoDeformacao ?? 0.28
  const tc = entrada.instanteContato

  const classeA = classeDoPerfil(perfilA)
  const classeB = config.classe ?? classeA
  const defA = CLASSES_VEICULO[classeA]
  const defB = CLASSES_VEICULO[classeB]
  const perfilB = perfilDaClasse(classeB)
  const [ex, , ez] = defB.escala
  const hwA = perfilA.largura / 2
  const hwB = (perfilB.largura / 2) * ex
  const frenteB = perfilB.frenteZ * ez
  const trasB = perfilB.traseiraZ * ez

  const quadro = amostrarCinematica(tipo, tc, entrada.velocidadeEventoKmh)
  const posA = posicaoDoVeiculo(perfilA, quadro)
  const poseA: Pose2D = { x: posA.x, z: posA.z, yaw: 0 }

  // quem se move: em "frente…" e na raspagem é A (velocidade do evento); nos demais, B bate em A parado
  const raspagem = arranjo === 'raspagemLateral'
  const frontal =
    raspagem || arranjo === 'frenteAFrente' || arranjo === 'frenteContraLateral' || arranjo === 'frenteContraTraseira'
  const vAKmh = frontal ? velocidadeEvento : 0
  const vBKmh = frontal
    ? arranjo === 'frenteContraTraseira'
      ? Math.min(config.velocidadeKmh, Math.max(0, velocidadeEvento - 5))
      : config.velocidadeKmh
    : entrada.secundario
      ? velocidadeEvento * 0.6
      : velocidadeEvento

  // sobreposição na frente: central, deslocada (40%) ou small overlap (25%)
  const larguraMenor = Math.min(perfilA.largura, perfilB.largura * ex)
  const sobreposicao =
    entrada.smallOverlap ? larguraMenor * 0.25 : config.alinhamento === 'deslocado' ? larguraMenor * 0.4 : null
  // o canto de A envolvido no deslocamento: +X, o mesmo lado do small overlap
  const cantoA = 1

  let poseB: Pose2D
  let contatoLocalA: { x: number; z: number }
  let regiaoA: RegiaoDanoVeicular
  let regiaoB: RegiaoDanoVeicular
  let normal: { x: number; z: number }
  let distanciaFaceB: number
  let direcaoB: { x: number; z: number }

  switch (arranjo) {
    case 'frenteAFrente': {
      const xB = sobreposicao != null ? poseA.x + cantoA * (hwA + hwB - sobreposicao) : poseA.x
      const xContato = sobreposicao != null ? poseA.x + cantoA * (hwA - sobreposicao / 2) : poseA.x
      poseB = { x: xB, z: poseA.z + perfilA.frenteZ + frenteB, yaw: Math.PI }
      contatoLocalA = { x: xContato - poseA.x, z: perfilA.frenteZ }
      regiaoA = 'frente'
      regiaoB = 'frente'
      normal = { x: 0, z: 1 }
      distanciaFaceB = frenteB
      direcaoB = { x: 0, z: -1 }
      break
    }
    case 'frenteContraTraseira': {
      const xB = sobreposicao != null ? poseA.x + cantoA * (hwA + hwB - sobreposicao) : poseA.x
      const xContato = sobreposicao != null ? poseA.x + cantoA * (hwA - sobreposicao / 2) : poseA.x
      poseB = { x: xB, z: poseA.z + perfilA.frenteZ - trasB, yaw: 0 }
      contatoLocalA = { x: xContato - poseA.x, z: perfilA.frenteZ }
      regiaoA = 'frente'
      regiaoB = 'traseira'
      normal = { x: 0, z: 1 }
      distanciaFaceB = -trasB
      direcaoB = { x: 0, z: 1 }
      break
    }
    case 'frenteContraLateral': {
      // B cruza a via no sentido −X; a lateral dele encara A
      const zPonto = zDoPontoLateral(perfilB, config.pontoLateral) * ez
      const xContato = sobreposicao != null ? poseA.x + cantoA * (hwA - sobreposicao / 2) : poseA.x
      poseB = { x: xContato + zPonto, z: poseA.z + perfilA.frenteZ + hwB, yaw: -Math.PI / 2 }
      contatoLocalA = { x: xContato - poseA.x, z: perfilA.frenteZ }
      regiaoA = 'frente'
      normal = { x: 0, z: 1 }
      distanciaFaceB = hwB
      direcaoB = { x: -1, z: 0 }
      // face de B voltada para A: a lateral −X local (ver `paraMundo2D`), o
      // lado do passageiro de B
      regiaoB = 'lateralDireita'
      break
    }
    case 'lateralRecebida': {
      const direcao = colisao(tipo).direcaoImpacto!
      // a força aponta para +X: o contato é na face −X, e vice-versa
      const face = direcao.x > 0 ? -1 : 1
      const zPonto = zDoPontoLateral(perfilA, config.pontoLateral)
      poseB = {
        x: poseA.x + face * (hwA + frenteB),
        z: poseA.z + zPonto,
        yaw: face < 0 ? Math.PI / 2 : -Math.PI / 2,
      }
      contatoLocalA = { x: face * hwA, z: zPonto }
      regiaoA = face > 0 ? 'lateralEsquerda' : 'lateralDireita'
      regiaoB = 'frente'
      normal = { x: face, z: 0 }
      distanciaFaceB = frenteB
      direcaoB = { x: -face, z: 0 }
      break
    }
    case 'raspagemLateral': {
      // B na faixa do lado do motorista (+X), fechando sobre A num ângulo pequeno
      const oposto = config.sentidoRaspagem === 'sentidoOposto'
      const yaw = oposto ? Math.PI + ANGULO_RASPAGEM_RAD : -ANGULO_RASPAGEM_RAD
      poseB = { x: poseA.x + hwA + hwB, z: poseA.z, yaw }
      contatoLocalA = { x: hwA, z: 0 }
      regiaoA = 'lateralEsquerda'
      // mesmo sentido: encosta o lado do passageiro de B; sentido oposto, o do motorista
      regiaoB = oposto ? 'lateralEsquerda' : 'lateralDireita'
      normal = { x: 1, z: 0 }
      distanciaFaceB = hwB
      direcaoB = { x: Math.sin(yaw), z: Math.cos(yaw) }
      break
    }
    case 'traseiraRecebida': {
      poseB = { x: poseA.x, z: poseA.z + perfilA.traseiraZ - frenteB, yaw: 0 }
      contatoLocalA = { x: 0, z: perfilA.traseiraZ }
      regiaoA = 'traseira'
      regiaoB = 'frente'
      normal = { x: 0, z: -1 }
      distanciaFaceB = frenteB
      direcaoB = { x: 0, z: 1 }
      break
    }
  }

  const contatoMundo = paraMundo2D(poseA, contatoLocalA.x, contatoLocalA.z)
  const contatoLocalB = doMundo2D(poseB, contatoMundo.x, contatoMundo.z)

  // ---- quantidade de movimento (m/s, no plano do solo)
  const mA = defA.massaKg
  const mB = defB.massaKg
  const M = mA + mB
  const vA = { x: 0, z: frontal ? vAKmh / 3.6 : 0 }
  const vB = { x: direcaoB.x * (vBKmh / 3.6), z: direcaoB.z * (vBKmh / 3.6) }
  const vRelN = (vA.x - vB.x) * normal.x + (vA.z - vB.z) * normal.z
  const aproximacao = Math.max(0, vRelN)
  const vc = { x: (mA * vA.x + mB * vB.x) / M, z: (mA * vA.z + mB * vB.z) / M }
  const tangente = { x: -normal.z, z: normal.x }
  const vcN = vc.x * normal.x + vc.z * normal.z
  const vcT = vc.x * tangente.x + vc.z * tangente.z
  const vBT = vB.x * tangente.x + vB.z * tangente.z
  // raspão: a lateral de B é rebatida e as chapas só se arrastam; nos demais,
  // o contato plástico quase iguala as velocidades
  const vBN = vB.x * normal.x + vB.z * normal.z
  const restituicao = raspagem ? RESTITUICAO_RASPAGEM : restituicaoEntreVeiculos(aproximacao * 3.6)
  // contato plástico: os dois saem com a velocidade do conjunto, e a parcela
  // elástica separa um do outro na proporção inversa das massas
  const vBDepoisN = raspagem
    ? Math.abs(vBN) * RESTITUICAO_RASPAGEM
    : vcN + restituicao * aproximacao * (mA / M)
  const vADepoisN = vcN - restituicao * aproximacao * (mB / M)
  const vBDepoisT = vBT - (vBT - vcT) * (raspagem ? ACOPLAMENTO_TANGENCIAL_RASPAGEM : 0.6)
  const velocidadeBDepois = vetor(
    normal.x * vBDepoisN + tangente.x * vBDepoisT,
    0,
    normal.z * vBDepoisN + tangente.z * vBDepoisT,
  )

  const deltaVA = aproximacao * (1 + restituicao) * (mB / M) * 3.6
  const deltaVB = aproximacao * (1 + restituicao) * (mA / M) * 3.6
  // velocidade tangencial relativa: o que "risca" e alonga a área de contato
  const vRelT = Math.abs((vA.x - vB.x) * tangente.x + (vA.z - vB.z) * tangente.z)

  // ---- dano de cada um: ΔV como velocidade equivalente de barreira (didático)
  const larguraContatoFrente = sobreposicao ?? (arranjo === 'frenteContraLateral' ? 1.45 : Math.min(larguraMenor - 0.1, 1.4))
  // na raspagem a área de contato se alonga com a velocidade tangencial
  const larguraLateral = raspagem ? Math.min(2.2, 0.9 + vRelT * 0.08) : 1.1
  // e a energia equivalente soma um pouco do arrasto tangencial ao pouco que fecha na normal
  const arrastoKmh = raspagem ? vRelT * 3.6 * 0.12 : 0
  const limitarRaspagem = (dano: DanoVeicular): DanoVeicular =>
    raspagem && dano.intrusaoM > INTRUSAO_MAXIMA_RASPAGEM_M
      ? { ...dano, intrusaoM: INTRUSAO_MAXIMA_RASPAGEM_M, nivel: 'leve' }
      : dano
  const danoA = limitarRaspagem(
    danoNaRegiao(regiaoA, 'outroVeiculo', (deltaVA + arrastoKmh) * FATOR_ESTRUTURA[regiaoA] * defB.agressividade, {
      intrusaoMinimaM: 0.04,
      larguraContatoM: regiaoA === 'frente' || regiaoA === 'traseira' ? larguraContatoFrente : larguraLateral,
    }),
  )
  const danoB = limitarRaspagem(
    danoNaRegiao(regiaoB, 'outroVeiculo', (deltaVB + arrastoKmh) * FATOR_ESTRUTURA[regiaoB] * defA.agressividade, {
      intrusaoMinimaM: 0.04,
      larguraContatoM: regiaoB === 'frente' || regiaoB === 'traseira' ? larguraContatoFrente : larguraLateral,
    }),
  )

  const idA = ID_VEICULO_PRINCIPAL
  const idB = entrada.secundario ? ID_OUTRO_VEICULO_SECUNDARIO : ID_OUTRO_VEICULO
  const assimetriaFrente = (xLocal: number, hw: number) =>
    sobreposicao != null ? limitar((xLocal / hw) * 0.8, -0.8, 0.8) : 0

  // tangencial de B arrasta a frente de A para o lado dele
  const arrasto = arranjo === 'frenteContraLateral' && aproximacao > 0 ? limitar(-vBT / (aproximacao + 1) * 0.5, -0.5, 0.5) : 0

  const descritorA = criarDescritorDeformacao(perfilA, danoA, {
    veiculoId: idA,
    instanteInicio: tc,
    duracao: duracaoDeformacao,
    centroFaceM: regiaoA === 'frente' || regiaoA === 'traseira' ? contatoLocalA.x : contatoLocalA.z,
    sinalLateral:
      regiaoA === 'lateralEsquerda' || regiaoA === 'lateralDireita'
        ? (Math.sign(contatoLocalA.x) as 1 | -1)
        : cantoA,
    alturaContatoY: defB.alturaParaChoqueY,
    smallOverlap: entrada.smallOverlap && regiaoA === 'frente',
    assimetria: regiaoA === 'frente' ? assimetriaFrente(contatoLocalA.x, hwA) + arrasto : 0,
    riscos: raspagem,
  })
  const xLocalB = contatoLocalB.x / ex
  const zLocalB = contatoLocalB.z / ez
  const lateralB = (regiaoB as RegiaoDanoVeicular).startsWith('lateral')
  const descritorB = criarDescritorDeformacao(perfilB, danoB, {
    veiculoId: idB,
    instanteInicio: tc,
    duracao: duracaoDeformacao,
    centroFaceM: lateralB ? zLocalB : xLocalB,
    sinalLateral: lateralB ? (Math.sign(xLocalB) as 1 | -1) : ((Math.sign(xLocalB) || 1) as 1 | -1),
    alturaContatoY: defA.alturaParaChoqueY / defB.escala[1],
    smallOverlap: Boolean(entrada.smallOverlap) && regiaoB === 'frente' && arranjo === 'frenteAFrente',
    assimetria: regiaoB === 'frente' || regiaoB === 'traseira' ? assimetriaFrente(xLocalB, hwB / ex) : 0,
    riscos: raspagem,
  })

  // ---- rotação: braço de alavanca × impulso (no plano)
  const impulsoB = { x: (velocidadeBDepois.x - vB.x) * mB, z: (velocidadeBDepois.z - vB.z) * mB }
  const bracoB = { x: contatoMundo.x - poseB.x, z: contatoMundo.z - poseB.z }
  const inerciaB = (mB * ((perfilB.comprimento * ez) ** 2 + (perfilB.largura * ex) ** 2)) / 12
  // (r × J)·ŷ = r.z·J.x − r.x·J.z
  const guinadaBRadPorS = limitar(((bracoB.z * impulsoB.x - bracoB.x * impulsoB.z) / inerciaB) * 0.6, -2.4, 2.4)

  // A: só nos arranjos em que A é quem se move de frente. A timeline de A é a de
  // uma barreira — leva A ao repouso —, então o ajuste é a velocidade que A de
  // fato tem depois do impacto: adiante junto com um B parado ou mais leve, para
  // trás no frente a frente de iguais. Mais a parcela lateral e a guinada do offset.
  let velocidadeExtraA = vetor(0, 0, 0)
  let guinadaARadPorS = 0
  if (frontal) {
    // A ganha o que B perde (quantidade de movimento); na tangente, o arrasto do contato
    const empurraoN = raspagem ? (-(vBDepoisN - vBN) * mB) / mA : vADepoisN
    const arrastoT = (-(vBDepoisT - vBT) * mB) / mA
    velocidadeExtraA = vetor(
      normal.x * empurraoN + tangente.x * arrastoT,
      0,
      normal.z * empurraoN + tangente.z * arrastoT,
    )
    const impulsoA = { x: -impulsoB.x, z: -impulsoB.z }
    const bracoA = { x: contatoMundo.x - poseA.x, z: contatoMundo.z - poseA.z }
    const inerciaA = (mA * (perfilA.comprimento ** 2 + perfilA.largura ** 2)) / 12
    // o small overlap já tem guinada própria (`variacoesCinematica`)
    guinadaARadPorS = entrada.smallOverlap
      ? 0
      : limitar(((bracoA.z * impulsoA.x - bracoA.x * impulsoA.z) / inerciaA) * 0.45, -1.2, 1.2)
  }

  const nomeA = defA.nome.toLowerCase()
  const nomeB = defB.nome.toLowerCase()
  const leitura =
    `Velocidade relativa ≈ ${Math.round(aproximacao * 3.6)} km/h. ` +
    `ΔV aproximado: ${nomeA} (A) ${Math.round(deltaVA)} km/h, ${nomeB} (B) ${Math.round(deltaVB)} km/h — ` +
    'quem tem menos massa varia mais de velocidade e deforma mais. Modelo visual didático, não reconstrução pericial.'

  return {
    arranjo,
    tipo,
    velocidadeEventoKmh: velocidadeEvento,
    idA,
    idB,
    classeA,
    classeB,
    perfilA,
    perfilB,
    escalaB: defB.escala,
    massaAKg: mA,
    massaBKg: mB,
    velocidadeAKmh: vAKmh,
    velocidadeBKmh: vBKmh,
    velocidadeRelativaKmh: aproximacao * 3.6,
    deltaVAKmh: deltaVA,
    deltaVBKmh: deltaVB,
    danoA,
    danoB,
    descritorA,
    descritorB,
    pontoContato: vetor(contatoMundo.x, 0, contatoMundo.z),
    normal: vetor(normal.x, 0, normal.z),
    poseBContato: poseB,
    distanciaFaceB,
    contatoLocalA,
    velocidadeBAntes: vetor(vB.x, 0, vB.z),
    velocidadeBDepois,
    guinadaBRadPorS,
    velocidadeExtraA,
    guinadaARadPorS,
    instanteContato: tc,
    duracaoDeformacao,
    duracaoRealS: Math.max(duracaoRealS(tipo, entrada.velocidadeEventoKmh), 0.2),
    leitura,
  }
}

/**
 * Até onde um veículo desliza depois do impacto, no máximo. Recurso de cena:
 * rodas travadas e chapa arrastando param o carro depressa, e a aula precisa
 * da posição final dentro do enquadramento.
 */
const DESLIZAMENTO_MAXIMO_M = 3.2

/** Desaceleração: atrito, ou o que for preciso para parar dentro do limite. */
const desaceleracao = (v0: number): number => Math.max(ATRITO_G, (v0 * v0) / (2 * DESLIZAMENTO_MAXIMO_M))

/** Deslocamento de um corpo que desliza com desaceleração constante até parar. */
function deslizamento(v0: number, t: number): number {
  if (t <= 0) return 0
  const a = desaceleracao(v0)
  const parada = Math.abs(v0) / a
  const tt = Math.min(t, parada)
  return Math.sign(v0) * (Math.abs(v0) * tt - (a * tt * tt) / 2)
}

const guinadaAmortecida = (taxa: number, t: number): number =>
  t <= 0 ? 0 : taxa * TAU_GUINADA_S * (1 - Math.exp(-t / TAU_GUINADA_S))

const intrusaoNoTempo = (descritor: DescritorDeformacaoVeicular, tempo: number): number => {
  const p = limitar((tempo - descritor.instanteInicio) / Math.max(descritor.duracao, 1e-6), 0, 1)
  return descritor.intrusaoMaxM * Math.sin((p * Math.PI) / 2)
}

/** Amostras da tabela do movimento pós-impacto de A, na régua 0..1. */
const AMOSTRAS_AJUSTE_A = 400

interface TabelaAjusteA {
  /** velocidade extra de A, em m/s, ao longo de `direcao` */
  velocidade: Float64Array
  /** deslocamento extra acumulado, em m */
  deslocamento: Float64Array
  direcao: { x: number; z: number }
}

const tabelasAjusteA = new WeakMap<ColisaoEntreVeiculos, TabelaAjusteA>()

/**
 * Movimento extra de A depois do contato, tabelado uma vez por colisão.
 *
 * A timeline de A freia o carro até parar, como numa barreira. A velocidade que
 * A de fato leva depois do impacto (`velocidadeExtraA`) entra na MESMA
 * proporção em que a timeline freia — zero no contato, inteira quando ela para —
 * e daí em diante desliza com atrito. Somadas, as duas nunca dão tranco: A só
 * chega à velocidade final quando o esmagamento terminou.
 */
function tabelaAjusteA(resolucao: ColisaoEntreVeiculos): TabelaAjusteA {
  const pronta = tabelasAjusteA.get(resolucao)
  if (pronta) return pronta
  const v = resolucao.velocidadeExtraA
  const alvo = Math.hypot(v.x, v.z)
  const direcao = alvo > 1e-9 ? { x: v.x / alvo, z: v.z / alvo } : { x: 0, z: 0 }
  const velocidade = new Float64Array(AMOSTRAS_AJUSTE_A + 1)
  const deslocamento = new Float64Array(AMOSTRAS_AJUSTE_A + 1)
  const T = resolucao.duracaoRealS
  const t0 = resolucao.instanteContato
  const v0 = Math.max(velocidadeRealMs(resolucao.tipo, 0, resolucao.velocidadeEventoKmh), 1e-6)
  const atrito = desaceleracao(alvo)
  let parada: number | null = null
  for (let i = 0; i <= AMOSTRAS_AJUSTE_A; i += 1) {
    const t = i / AMOSTRAS_AJUSTE_A
    let atual = 0
    if (alvo > 1e-9 && t > t0) {
      if (parada === null) {
        const freado = limitar(1 - velocidadeRealMs(resolucao.tipo, t, resolucao.velocidadeEventoKmh) / v0, 0, 1)
        atual = alvo * freado
        if (freado >= 0.999) parada = t
      } else {
        atual = Math.max(0, alvo - atrito * (t - parada) * T)
      }
    }
    velocidade[i] = atual
    deslocamento[i] = i === 0 ? 0 : deslocamento[i - 1]! + ((velocidade[i - 1]! + atual) / 2) * (T / AMOSTRAS_AJUSTE_A)
  }
  const tabela = { velocidade, deslocamento, direcao }
  tabelasAjusteA.set(resolucao, tabela)
  return tabela
}

const interpolarTabela = (valores: Float64Array, tempo: number): number => {
  const x = limitar(tempo, 0, 1) * AMOSTRAS_AJUSTE_A
  const i = Math.min(Math.floor(x), AMOSTRAS_AJUSTE_A - 1)
  const f = x - i
  return valores[i]! + (valores[i + 1]! - valores[i]!) * f
}

/**
 * Ajuste pós-impacto de A (empurrão e guinada), no mundo.
 *
 * Zero antes do contato; cresce enquanto a timeline freia A e depois desliza
 * com atrito até parar. Determinístico: voltar a timeline devolve o mesmo ajuste.
 */
export function ajustePosImpactoA(
  resolucao: ColisaoEntreVeiculos | null | undefined,
  tempo: number,
): { deslocamento: Vetor3; guinadaRad: number } {
  if (!resolucao) return { deslocamento: vetor(0, 0, 0), guinadaRad: 0 }
  const T = resolucao.duracaoRealS
  const dt = Math.max(0, tempo - resolucao.instanteContato) * T
  const tabela = tabelaAjusteA(resolucao)
  const percorrido = interpolarTabela(tabela.deslocamento, tempo)
  return {
    deslocamento: vetor(tabela.direcao.x * percorrido, 0, tabela.direcao.z * percorrido),
    guinadaRad: limitar(guinadaAmortecida(resolucao.guinadaARadPorS, dt), -0.45, 0.45),
  }
}

/** Velocidade extra de A no instante, em m/s no mundo — a derivada de `ajustePosImpactoA`. */
export function velocidadeAjusteA(resolucao: ColisaoEntreVeiculos | null | undefined, tempo: number): Vetor3 {
  if (!resolucao) return vetor(0, 0, 0)
  const tabela = tabelaAjusteA(resolucao)
  const v = interpolarTabela(tabela.velocidade, tempo)
  return vetor(tabela.direcao.x * v, 0, tabela.direcao.z * v)
}

export interface PoseOutroVeiculo {
  posicao: Vetor3
  guinadaRad: number
  /** fase didática do segundo veículo */
  fase: 'aproximacao' | 'compressao' | 'separacao' | 'repouso'
}

/**
 * Pose de B no mundo, num instante da timeline.
 *
 * aproximação → primeiro contato → compressão (B acompanha a face de A, que
 * recua com o esmagamento dos dois) → separação com a velocidade pós-impacto
 * e guinada → repouso. B nunca atravessa A: a posição ao longo da normal é o
 * máximo entre o movimento livre de B e "encostado na face atual de A".
 */
export function amostrarPoseOutroVeiculo(resolucao: ColisaoEntreVeiculos, tempo: number): PoseOutroVeiculo {
  const { normal, poseBContato: pose, instanteContato: tc, duracaoDeformacao: dur } = resolucao
  const T = resolucao.duracaoRealS
  const tangente = { x: -normal.z, z: normal.x }
  const tSep = tc + dur

  // face de contato de A no instante, ao longo da normal (já com o recuo dos dois esmagamentos)
  const faceNaNormal = (t: number): number => {
    const quadro = amostrarCinematica(resolucao.tipo, t, resolucao.velocidadeEventoKmh)
    const posA = posicaoDoVeiculo(resolucao.perfilA, quadro)
    const extra = ajustePosImpactoA(resolucao, t).deslocamento
    const cx = posA.x + extra.x + resolucao.contatoLocalA.x
    const cz = posA.z + extra.z + resolucao.contatoLocalA.z
    const face = cx * normal.x + cz * normal.z
    return face - intrusaoNoTempo(resolucao.descritorA, t) - intrusaoNoTempo(resolucao.descritorB, t)
  }
  const centroEncostadoN = (t: number) => faceNaNormal(t) + resolucao.distanciaFaceB

  const cN0 = pose.x * normal.x + pose.z * normal.z
  const cT0 = pose.x * tangente.x + pose.z * tangente.z
  const vAntes = resolucao.velocidadeBAntes
  const vAntesN = vAntes.x * normal.x + vAntes.z * normal.z
  const vAntesT = vAntes.x * tangente.x + vAntes.z * tangente.z
  const vDepois = resolucao.velocidadeBDepois
  const vDepoisN = vDepois.x * normal.x + vDepois.z * normal.z
  const vDepoisT = vDepois.x * tangente.x + vDepois.z * tangente.z

  let n: number
  let tt: number
  let guinada = 0
  let fase: PoseOutroVeiculo['fase']

  if (tempo < tc) {
    const dt = (tempo - tc) * T
    // aproximação a velocidade constante; limitada para B não nascer fora da cena
    const recuo = Math.max(dt, -DISTANCIA_MAXIMA_APROXIMACAO_M / Math.max(Math.hypot(vAntes.x, vAntes.z), 1e-6))
    n = cN0 + vAntesN * recuo
    tt = cT0 + vAntesT * recuo
    // nunca dentro de A: se A anda na direção de B (frente × traseira), B vai à frente
    n = Math.max(n, centroEncostadoN(tempo))
    fase = 'aproximacao'
  } else if (tempo <= tSep) {
    const f = (tempo - tc) / Math.max(dur, 1e-6)
    const dt = (tempo - tc) * T
    n = centroEncostadoN(tempo)
    tt = cT0 + (vAntesT + (vDepoisT - vAntesT) * f * 0.5) * dt
    guinada = resolucao.guinadaBRadPorS * f * f * dur * T * 0.5
    fase = 'compressao'
  } else {
    const dtComp = dur * T
    const nSep = centroEncostadoN(tSep)
    const tSepT = cT0 + (vAntesT + (vDepoisT - vAntesT) * 0.5) * dtComp
    const guinadaSep = resolucao.guinadaBRadPorS * dur * T * 0.5
    const dt = (tempo - tSep) * T
    // direção da deslizada: a velocidade pós-impacto inteira, desacelerando junto
    const vel = Math.hypot(vDepoisN, vDepoisT)
    const percorrido = vel > 1e-6 ? deslizamento(vel, dt) : 0
    const livreN = nSep + (vel > 1e-6 ? (vDepoisN / vel) * percorrido : 0)
    n = Math.max(livreN, centroEncostadoN(tempo))
    tt = tSepT + (vel > 1e-6 ? (vDepoisT / vel) * percorrido : 0)
    guinada = guinadaSep + guinadaAmortecida(resolucao.guinadaBRadPorS, dt)
    fase = vel > 1e-6 && dt < vel / desaceleracao(vel) ? 'separacao' : 'repouso'
  }

  return {
    posicao: vetor(normal.x * n + tangente.x * tt, 0, normal.z * n + tangente.z * tt),
    guinadaRad: pose.yaw + guinada,
    fase,
  }
}
